<template>
  <v-list-item
    :id="deadlineId"
    :class="[
      'deadline-list-item',
      { 'highlight-element': isHighlighted },
      { 'deadline-closed': isClosed },
    ]"
    :style="accentBorderStyle"
  >
    <template #prepend>
      <v-avatar size="32" :style="avatarStyle" class="elevation-1">
        <v-icon v-if="isClosed" size="18" color="white"
          >mdi-check-circle</v-icon
        >
        <v-icon v-else-if="categoryIcon" size="18" color="white">{{
          categoryIcon
        }}</v-icon>
        <template v-else>{{ days }}</template>
      </v-avatar>
    </template>

    <template #default>
      <div class="deadline-content">
        <div class="header-row">
          <div
            class="deadline-description"
            v-html="$sanitizeHtml(deadline.description)"
          ></div>
        </div>

        <div class="meta-row">
          <v-chip
            v-if="categoryName"
            size="small"
            class="category-chip text-white"
            :style="categoryChipStyle"
            :prepend-icon="categoryIcon || 'mdi-tag'"
          >
            {{ categoryName }}
          </v-chip>

          <v-chip
            v-if="isClosed"
            size="small"
            class="closed-chip"
            prepend-icon="mdi-check-circle"
          >
            Gesloten {{ closedDate }}
          </v-chip>

          <UserChip
            v-if="hasUser && userFromStore"
            :user="userFromStore"
            :on-light="true"
          />

          <AccountChip
            v-if="show_account && deadline.account_id"
            :id="deadline.account_id"
            :name="deadline.account_name"
            :icon="deadline.account_icon"
            :short="deadline.account_short"
          />

          <v-chip
            v-if="humanDuration"
            size="small"
            class="duration-chip"
            prepend-icon="mdi-timer-outline"
          >
            <span class="chip-text">{{ humanDuration }}</span>
          </v-chip>

          <v-chip size="small" class="date-chip" prepend-icon="mdi-calendar">
            <span class="chip-text">{{ formattedDate }}</span>
          </v-chip>
        </div>
      </div>
    </template>

    <template #append>
      <ContextMenu :options="deadlineOptions" />
    </template>
  </v-list-item>
  <v-divider></v-divider>
</template>

<script>
import AccountChip from "../accounts/AccountChip.vue";
import ContextMenu from "../ui/ContextMenu.vue";
import { computed, onMounted, watch } from "vue";
import moment from "moment";
import { useDeadlinesStoreRefactor } from "../../stores/deadlinesStoreRefactor";
import { useFormsStore } from "../../stores/formsStore";
import { useUsersStoreRefactor } from "../../stores/usersStoreRefactor";
import { useAlertStore } from "../../stores/alertStore";
import { useDeadlineCategoriesStoreRefactor } from "../../stores/deadlineCategoriesStoreRefactor";
import { useRoute } from "vue-router";
import UserChip from "../users/UserChip.vue";

export default {
  name: "DeadlineListItem",
  components: { AccountChip, ContextMenu, UserChip },
  props: ["deadline", "show_account"],
  setup(props) {
    const deadlinesStore = useDeadlinesStoreRefactor();
    const formsStore = useFormsStore();
    const usersStore = useUsersStoreRefactor();
    const categoriesStore = useDeadlineCategoriesStoreRefactor();
    const alertStore = useAlertStore();
    const route = useRoute();

    const isClosed = computed(() => !!props.deadline.completed_at);

    const days = computed(() => {
      const b = moment().startOf("day");
      const a = moment(props.deadline.deadline);
      return a.diff(b, "days");
    });

    const formattedDate = computed(() =>
      moment(props.deadline.deadline).format("DD-MM-YYYY"),
    );
    const closedDate = computed(() =>
      props.deadline.completed_at
        ? moment(props.deadline.completed_at).format("DD-MM-YYYY")
        : "",
    );

    const hasUser = computed(
      () => !!props.deadline.user_id && props.deadline.user_id !== null,
    );

    const userFromStore = computed(() => {
      if (!hasUser.value) return null;
      if (!usersStore.users || !Array.isArray(usersStore.users)) {
        if (!usersStore.loading) usersStore.getAll();
        return null;
      }
      return (
        usersStore.users.find((u) => u.id === props.deadline.user_id) || null
      );
    });

    const category = computed(() => {
      const id = props.deadline.deadline_category_id;
      return categoriesStore.categories.find((c) => c.id === id) || null;
    });

    const categoryName = computed(() => category.value?.name || null);
    const categoryIcon = computed(() => category.value?.icon || null);
    const categoryColor = computed(() => category.value?.color || null);

    const categoryChipStyle = computed(() => ({
      backgroundColor: categoryColor.value || "#607D8B",
      color: "#fff",
    }));

    const avatarStyle = computed(() => ({
      backgroundColor: isClosed.value
        ? "#9E9E9E"
        : categoryColor.value ||
          (days.value > 0
            ? "#4CAF50"
            : days.value === 0
              ? "#FF9800"
              : "#F44336"),
      color: "#fff",
    }));

    const accentBorderStyle = computed(() => ({
      borderLeft: `4px solid ${isClosed.value ? "#9E9E9E" : days.value > 1 ? "#4CAF50" : days.value >= 0 ? "#FF9800" : "#F44336"}`,
    }));

    const humanDuration = computed(() => {
      const seconds = props.deadline.duration_seconds;
      if (seconds == null) return null;
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      if (h && m) return `${h}u ${m}m`;
      if (h) return `${h}u`;
      if (m) return `${m}m`;
      return "0m";
    });

    const canClose = computed(() => !isClosed.value);

    const deadlineOptions = computed(() => {
      const options = [
        {
          name: "Deadline wijzigen",
          action: () => editDeadline(),
          icon: "mdi-playlist-edit",
        },
      ];
      if (canClose.value)
        options.push({
          name: "Deadline sluiten",
          action: () => closeDeadline(),
          icon: "mdi-check-circle",
        });
      options.push({
        name: "Verwijderen",
        action: () => destroyDeadline(),
        icon: "mdi-delete",
      });
      return options;
    });

    const deadlineId = computed(() => `d${props.deadline.id}`);

    const isHighlighted = computed(
      () =>
        route.query.deadline &&
        Number(route.query.deadline) === Number(props.deadline.id),
    );

    const editDeadline = () => {
      formsStore.edit({ type: "deadline", item: props.deadline });
    };

    const closeDeadline = async () => {
      try {
        await deadlinesStore.close(props.deadline.id);
        alertStore.setAlert("Deadline gesloten", "success");
      } catch (e) {
        alertStore.setAlert("Kon deadline niet sluiten", "error");
      }
    };

    const destroyDeadline = async () => {
      try {
        if (!confirm(`Weet je zeker dat je deze taak wilt verwijderen?`))
          return;
        await deadlinesStore.destroy(props.deadline);
      } catch (error) {
        console.error(
          `DeadlineListItem: Error removing deadline ${props.deadline.id}:`,
          error,
        );
      }
    };

    onMounted(() => {
      if (hasUser.value && usersStore.users.length === 0) usersStore.getAll();
      if (!categoriesStore.categories.length) categoriesStore.fetchAll();
    });

    watch(
      () => route.query.deadline,
      (newDeadlineId) => {
        if (
          newDeadlineId &&
          Number(newDeadlineId) === Number(props.deadline.id)
        ) {
          setTimeout(() => {
            const element = document.getElementById(deadlineId.value);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              element.classList.add("highlight-deadline");
              setTimeout(() => {
                element.classList.remove("highlight-deadline");
              }, 2000);
            }
          }, 100);
        }
      },
    );

    return {
      isClosed,
      days,
      formattedDate,
      closedDate,
      hasUser,
      userFromStore,
      categoryName,
      categoryIcon,
      categoryChipStyle,
      avatarStyle,
      accentBorderStyle,
      humanDuration,
      deadlineOptions,
      editDeadline,
      destroyDeadline,
      closeDeadline,
      deadlineId,
      isHighlighted,
    };
  },
};
</script>

<style scoped>
.deadline-list-item {
  padding: 10px 8px;
  transition: background-color 0.3s ease;
  border-radius: 8px;
  margin: 4px 0;
}

.deadline-closed {
  opacity: 0.85;
}

.deadline-closed .deadline-description {
  text-decoration: line-through;
}

.deadline-content {
  width: 100%;
}

.header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.deadline-description {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  margin-bottom: 4px;
  line-height: 1.45;
  overflow-wrap: break-word;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.category-chip {
  color: #fff !important;
}
.duration-chip {
  background: #f1f5f9;
  color: #333;
}
.date-chip {
  background: #f1f5f9;
  color: #333;
}
.closed-chip {
  background: #e0e0e0;
  color: #333;
}

/* Highlight styles */
.highlight-element {
  background-color: rgba(25, 118, 210, 0.06);
}

/* Animation for highlighted deadline */
.highlight-deadline {
  animation: highlight-pulse 2s ease;
  background-color: rgba(25, 118, 210, 0.12) !important;
  border-radius: 8px !important;
  box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.12) !important;
  position: relative !important;
  z-index: 2 !important;
}

@keyframes highlight-pulse {
  0% {
    background-color: rgba(25, 118, 210, 0.12);
    transform: scale(1);
  }
  50% {
    background-color: rgba(25, 118, 210, 0.2);
    transform: scale(1.01);
  }
  100% {
    background-color: rgba(25, 118, 210, 0.12);
    transform: scale(1);
  }
}
</style>
